/**
 * Airport lookup for the route picker.
 *
 * A curated list, not a full 9,000-row IATA dump — that would add hundreds of
 * kilobytes to the bundle so people can search airfields with no scheduled
 * service. This covers the Gulf and Indian subcontinent densely (the corridor
 * the product exists for) and the rest of the world at hub level.
 *
 * A missing airport is an inconvenience rather than a wall: the picker accepts
 * any three-letter code, and the user can save it with a proper city and
 * country of their own (see ./customAirports). Those are merged in below, so
 * once added they behave exactly like a built-in entry — searchable, labelled,
 * and rendered by name rather than as a bare code.
 */
import { getCustomAirports } from './customAirports';

export const AIRPORTS = [
  // ── Gulf & Middle East ────────────────────────────────────────────────
  { code: 'BAH', city: 'Bahrain', country: 'Bahrain' },
  { code: 'DXB', city: 'Dubai', country: 'United Arab Emirates' },
  { code: 'DWC', city: 'Dubai (Al Maktoum)', country: 'United Arab Emirates' },
  { code: 'AUH', city: 'Abu Dhabi', country: 'United Arab Emirates' },
  { code: 'SHJ', city: 'Sharjah', country: 'United Arab Emirates' },
  { code: 'RKT', city: 'Ras Al Khaimah', country: 'United Arab Emirates' },
  { code: 'AAN', city: 'Al Ain', country: 'United Arab Emirates' },
  { code: 'DOH', city: 'Doha', country: 'Qatar' },
  { code: 'KWI', city: 'Kuwait City', country: 'Kuwait' },
  { code: 'MCT', city: 'Muscat', country: 'Oman' },
  { code: 'SLL', city: 'Salalah', country: 'Oman' },
  { code: 'RUH', city: 'Riyadh', country: 'Saudi Arabia' },
  { code: 'JED', city: 'Jeddah', country: 'Saudi Arabia' },
  { code: 'DMM', city: 'Dammam', country: 'Saudi Arabia' },
  { code: 'MED', city: 'Medina', country: 'Saudi Arabia' },
  { code: 'AMM', city: 'Amman', country: 'Jordan' },
  { code: 'BEY', city: 'Beirut', country: 'Lebanon' },
  { code: 'CAI', city: 'Cairo', country: 'Egypt' },
  { code: 'HRG', city: 'Hurghada', country: 'Egypt' },
  { code: 'SSH', city: 'Sharm El Sheikh', country: 'Egypt' },
  { code: 'BGW', city: 'Baghdad', country: 'Iraq' },
  { code: 'EBL', city: 'Erbil', country: 'Iraq' },
  { code: 'IKA', city: 'Tehran', country: 'Iran' },
  { code: 'TLV', city: 'Tel Aviv', country: 'Israel' },

  // ── India ─────────────────────────────────────────────────────────────
  { code: 'DEL', city: 'Delhi', country: 'India' },
  { code: 'BOM', city: 'Mumbai', country: 'India' },
  { code: 'MAA', city: 'Chennai', country: 'India' },
  { code: 'BLR', city: 'Bengaluru', country: 'India' },
  { code: 'HYD', city: 'Hyderabad', country: 'India' },
  { code: 'CCU', city: 'Kolkata', country: 'India' },
  { code: 'COK', city: 'Kochi', country: 'India' },
  { code: 'TRV', city: 'Thiruvananthapuram', country: 'India' },
  { code: 'CCJ', city: 'Kozhikode', country: 'India' },
  { code: 'CNN', city: 'Kannur', country: 'India' },
  { code: 'IXE', city: 'Mangaluru', country: 'India' },
  { code: 'GOI', city: 'Goa (Dabolim)', country: 'India' },
  { code: 'GOX', city: 'Goa (Mopa)', country: 'India' },
  { code: 'AMD', city: 'Ahmedabad', country: 'India' },
  { code: 'PNQ', city: 'Pune', country: 'India' },
  { code: 'JAI', city: 'Jaipur', country: 'India' },
  { code: 'LKO', city: 'Lucknow', country: 'India' },
  { code: 'PAT', city: 'Patna', country: 'India' },
  { code: 'BBI', city: 'Bhubaneswar', country: 'India' },
  { code: 'IXC', city: 'Chandigarh', country: 'India' },
  { code: 'ATQ', city: 'Amritsar', country: 'India' },
  { code: 'VNS', city: 'Varanasi', country: 'India' },
  { code: 'NAG', city: 'Nagpur', country: 'India' },
  { code: 'IDR', city: 'Indore', country: 'India' },
  { code: 'TRZ', city: 'Tiruchirappalli', country: 'India' },
  { code: 'IXM', city: 'Madurai', country: 'India' },
  { code: 'TIR', city: 'Tirupati', country: 'India' },
  { code: 'VTZ', city: 'Visakhapatnam', country: 'India' },
  { code: 'VGA', city: 'Vijayawada', country: 'India' },
  { code: 'GAU', city: 'Guwahati', country: 'India' },
  { code: 'IXB', city: 'Bagdogra', country: 'India' },
  { code: 'IXR', city: 'Ranchi', country: 'India' },
  { code: 'JDH', city: 'Jodhpur', country: 'India' },
  { code: 'UDR', city: 'Udaipur', country: 'India' },
  { code: 'IXJ', city: 'Jammu', country: 'India' },
  { code: 'SXR', city: 'Srinagar', country: 'India' },
  { code: 'DED', city: 'Dehradun', country: 'India' },
  { code: 'STV', city: 'Surat', country: 'India' },
  { code: 'RAJ', city: 'Rajkot', country: 'India' },
  { code: 'BDQ', city: 'Vadodara', country: 'India' },
  { code: 'BHO', city: 'Bhopal', country: 'India' },
  { code: 'RPR', city: 'Raipur', country: 'India' },
  { code: 'HBX', city: 'Hubballi', country: 'India' },
  { code: 'IXZ', city: 'Port Blair', country: 'India' },

  // ── South Asia ────────────────────────────────────────────────────────
  { code: 'CMB', city: 'Colombo', country: 'Sri Lanka' },
  { code: 'MLE', city: 'Malé', country: 'Maldives' },
  { code: 'KTM', city: 'Kathmandu', country: 'Nepal' },
  { code: 'DAC', city: 'Dhaka', country: 'Bangladesh' },
  { code: 'CGP', city: 'Chattogram', country: 'Bangladesh' },
  { code: 'ISB', city: 'Islamabad', country: 'Pakistan' },
  { code: 'KHI', city: 'Karachi', country: 'Pakistan' },
  { code: 'LHE', city: 'Lahore', country: 'Pakistan' },
  { code: 'PEW', city: 'Peshawar', country: 'Pakistan' },
  { code: 'MUX', city: 'Multan', country: 'Pakistan' },
  { code: 'SKT', city: 'Sialkot', country: 'Pakistan' },
  { code: 'KBL', city: 'Kabul', country: 'Afghanistan' },

  // ── South-East & East Asia ────────────────────────────────────────────
  { code: 'SIN', city: 'Singapore', country: 'Singapore' },
  { code: 'KUL', city: 'Kuala Lumpur', country: 'Malaysia' },
  { code: 'BKK', city: 'Bangkok (Suvarnabhumi)', country: 'Thailand' },
  { code: 'DMK', city: 'Bangkok (Don Mueang)', country: 'Thailand' },
  { code: 'HKT', city: 'Phuket', country: 'Thailand' },
  { code: 'CGK', city: 'Jakarta', country: 'Indonesia' },
  { code: 'DPS', city: 'Bali (Denpasar)', country: 'Indonesia' },
  { code: 'MNL', city: 'Manila', country: 'Philippines' },
  { code: 'HAN', city: 'Hanoi', country: 'Vietnam' },
  { code: 'SGN', city: 'Ho Chi Minh City', country: 'Vietnam' },
  { code: 'PNH', city: 'Phnom Penh', country: 'Cambodia' },
  { code: 'RGN', city: 'Yangon', country: 'Myanmar' },
  { code: 'HKG', city: 'Hong Kong', country: 'Hong Kong' },
  { code: 'TPE', city: 'Taipei', country: 'Taiwan' },
  { code: 'PVG', city: 'Shanghai (Pudong)', country: 'China' },
  { code: 'PEK', city: 'Beijing (Capital)', country: 'China' },
  { code: 'PKX', city: 'Beijing (Daxing)', country: 'China' },
  { code: 'CAN', city: 'Guangzhou', country: 'China' },
  { code: 'ICN', city: 'Seoul (Incheon)', country: 'South Korea' },
  { code: 'NRT', city: 'Tokyo (Narita)', country: 'Japan' },
  { code: 'HND', city: 'Tokyo (Haneda)', country: 'Japan' },
  { code: 'KIX', city: 'Osaka (Kansai)', country: 'Japan' },

  // ── Europe ────────────────────────────────────────────────────────────
  { code: 'LHR', city: 'London (Heathrow)', country: 'United Kingdom' },
  { code: 'LGW', city: 'London (Gatwick)', country: 'United Kingdom' },
  { code: 'STN', city: 'London (Stansted)', country: 'United Kingdom' },
  { code: 'MAN', city: 'Manchester', country: 'United Kingdom' },
  { code: 'BHX', city: 'Birmingham', country: 'United Kingdom' },
  { code: 'EDI', city: 'Edinburgh', country: 'United Kingdom' },
  { code: 'DUB', city: 'Dublin', country: 'Ireland' },
  { code: 'CDG', city: 'Paris (Charles de Gaulle)', country: 'France' },
  { code: 'ORY', city: 'Paris (Orly)', country: 'France' },
  { code: 'AMS', city: 'Amsterdam', country: 'Netherlands' },
  { code: 'FRA', city: 'Frankfurt', country: 'Germany' },
  { code: 'MUC', city: 'Munich', country: 'Germany' },
  { code: 'BER', city: 'Berlin', country: 'Germany' },
  { code: 'DUS', city: 'Düsseldorf', country: 'Germany' },
  { code: 'ZRH', city: 'Zurich', country: 'Switzerland' },
  { code: 'GVA', city: 'Geneva', country: 'Switzerland' },
  { code: 'VIE', city: 'Vienna', country: 'Austria' },
  { code: 'BRU', city: 'Brussels', country: 'Belgium' },
  { code: 'CPH', city: 'Copenhagen', country: 'Denmark' },
  { code: 'ARN', city: 'Stockholm', country: 'Sweden' },
  { code: 'OSL', city: 'Oslo', country: 'Norway' },
  { code: 'HEL', city: 'Helsinki', country: 'Finland' },
  { code: 'MAD', city: 'Madrid', country: 'Spain' },
  { code: 'BCN', city: 'Barcelona', country: 'Spain' },
  { code: 'LIS', city: 'Lisbon', country: 'Portugal' },
  { code: 'FCO', city: 'Rome (Fiumicino)', country: 'Italy' },
  { code: 'MXP', city: 'Milan (Malpensa)', country: 'Italy' },
  { code: 'ATH', city: 'Athens', country: 'Greece' },
  { code: 'IST', city: 'Istanbul', country: 'Türkiye' },
  { code: 'SAW', city: 'Istanbul (Sabiha Gökçen)', country: 'Türkiye' },
  { code: 'WAW', city: 'Warsaw', country: 'Poland' },
  { code: 'PRG', city: 'Prague', country: 'Czechia' },
  { code: 'BUD', city: 'Budapest', country: 'Hungary' },
  { code: 'OTP', city: 'Bucharest', country: 'Romania' },
  { code: 'SVO', city: 'Moscow (Sheremetyevo)', country: 'Russia' },
  { code: 'TAS', city: 'Tashkent', country: 'Uzbekistan' },
  { code: 'ALA', city: 'Almaty', country: 'Kazakhstan' },
  { code: 'GYD', city: 'Baku', country: 'Azerbaijan' },
  { code: 'TBS', city: 'Tbilisi', country: 'Georgia' },

  // ── North America ─────────────────────────────────────────────────────
  { code: 'JFK', city: 'New York (JFK)', country: 'United States' },
  { code: 'EWR', city: 'New York (Newark)', country: 'United States' },
  { code: 'BOS', city: 'Boston', country: 'United States' },
  { code: 'IAD', city: 'Washington (Dulles)', country: 'United States' },
  { code: 'ORD', city: 'Chicago (O’Hare)', country: 'United States' },
  { code: 'ATL', city: 'Atlanta', country: 'United States' },
  { code: 'DFW', city: 'Dallas–Fort Worth', country: 'United States' },
  { code: 'IAH', city: 'Houston', country: 'United States' },
  { code: 'LAX', city: 'Los Angeles', country: 'United States' },
  { code: 'SFO', city: 'San Francisco', country: 'United States' },
  { code: 'SEA', city: 'Seattle', country: 'United States' },
  { code: 'DEN', city: 'Denver', country: 'United States' },
  { code: 'MIA', city: 'Miami', country: 'United States' },
  { code: 'YYZ', city: 'Toronto', country: 'Canada' },
  { code: 'YVR', city: 'Vancouver', country: 'Canada' },
  { code: 'YUL', city: 'Montreal', country: 'Canada' },
  { code: 'MEX', city: 'Mexico City', country: 'Mexico' },

  // ── Africa ────────────────────────────────────────────────────────────
  { code: 'JNB', city: 'Johannesburg', country: 'South Africa' },
  { code: 'CPT', city: 'Cape Town', country: 'South Africa' },
  { code: 'NBO', city: 'Nairobi', country: 'Kenya' },
  { code: 'ADD', city: 'Addis Ababa', country: 'Ethiopia' },
  { code: 'LOS', city: 'Lagos', country: 'Nigeria' },
  { code: 'ACC', city: 'Accra', country: 'Ghana' },
  { code: 'CMN', city: 'Casablanca', country: 'Morocco' },
  { code: 'TUN', city: 'Tunis', country: 'Tunisia' },
  { code: 'ALG', city: 'Algiers', country: 'Algeria' },
  { code: 'DAR', city: 'Dar es Salaam', country: 'Tanzania' },
  { code: 'EBB', city: 'Entebbe', country: 'Uganda' },
  { code: 'KRT', city: 'Khartoum', country: 'Sudan' },
  { code: 'MRU', city: 'Mauritius', country: 'Mauritius' },
  { code: 'SEZ', city: 'Seychelles', country: 'Seychelles' },

  // ── Oceania & South America ───────────────────────────────────────────
  { code: 'SYD', city: 'Sydney', country: 'Australia' },
  { code: 'MEL', city: 'Melbourne', country: 'Australia' },
  { code: 'BNE', city: 'Brisbane', country: 'Australia' },
  { code: 'PER', city: 'Perth', country: 'Australia' },
  { code: 'AKL', city: 'Auckland', country: 'New Zealand' },
  { code: 'GRU', city: 'São Paulo', country: 'Brazil' },
  { code: 'EZE', city: 'Buenos Aires', country: 'Argentina' },
  { code: 'SCL', city: 'Santiago', country: 'Chile' },
  { code: 'BOG', city: 'Bogotá', country: 'Colombia' },
  { code: 'LIM', city: 'Lima', country: 'Peru' },
];

const BY_CODE = new Map(AIRPORTS.map((a) => [a.code, a]));

/** Built-ins plus anything the user has added. Custom entries win on a clash. */
export function allAirports() {
  const custom = getCustomAirports();
  if (!custom.length) return AIRPORTS;
  const overridden = new Set(custom.map((a) => a.code));
  return [...custom, ...AIRPORTS.filter((a) => !overridden.has(a.code))];
}

export function airportByCode(code) {
  const c = String(code || '').toUpperCase();
  return getCustomAirports().find((a) => a.code === c) || BY_CODE.get(c) || null;
}

export function airportLabel(code) {
  const a = airportByCode(code);
  return a ? `${a.city}, ${a.country}` : '';
}

/**
 * Rank matches so typing an exact code puts it first, then code prefixes, then
 * city, then country. Without the ordering, "IN" would surface a dozen Indian
 * cities before India itself, and "DEL" would not necessarily put Delhi on top.
 */
export function searchAirports(query, limit = Infinity) {
  const q = String(query || '').trim().toLowerCase();
  // No cap by default. The previous limit of 60 meant an empty query showed
  // only the first 60 entries and scrolling revealed nothing more — the list
  // looked far shorter than it is. A few hundred rows render fine.
  const pool = allAirports();
  if (!q) return limit === Infinity ? pool : pool.slice(0, limit);
  const scored = [];
  for (const a of pool) {
    const code = a.code.toLowerCase();
    const city = a.city.toLowerCase();
    const country = a.country.toLowerCase();
    let score = -1;
    if (code === q) score = 0;
    else if (code.startsWith(q)) score = 1;
    else if (city.startsWith(q)) score = 2;
    else if (country.startsWith(q)) score = 3;
    else if (city.includes(q)) score = 4;
    else if (country.includes(q)) score = 5;
    if (score >= 0) scored.push([score, a]);
  }
  const ranked = scored.sort((x, y) => x[0] - y[0] || x[1].city.localeCompare(y[1].city)).map(([, a]) => a);
  return limit === Infinity ? ranked : ranked.slice(0, limit);
}
